import React, {
  useRef,
  useState,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import I18n from 'i18n-js';
import { useNavigate } from 'react-router-dom';
import { useDrag } from '@use-gesture/react';
import { animated, useSpring } from '@react-spring/web';
import { Button, Message } from 'grab-ui';
import scribeTrack from 'scribe/scribe-track';
import { inGrabApp } from 'common/utils/platform';
import instrumentationDuck from 'ducks/instrumentation-duck';
import mapDuck from 'ducks/map-duck';
import Routes from 'Routes';
import ExpectationManagement from '../expectation-management';
import styles from './drag-sheet.less';
import arrowDownSvg from '../../public/images/arrow-down.svg';
import useMediaQuery from '../../hooks/useMediaQuery';
import { useViewPort } from '../../context/view-port';
import { isPendingStatus } from '../../common/utils/status';
import { DEFAULT_MESSAGE_MARGIN_TOP } from '../../constants/message';

const getDimensionFromRef = ref => {
  if (!ref.current) return { width: 0, height: 0 };

  return {
    width: ref.current.clientWidth,
    height: ref.current.clientHeight,
  };
};

const DragSheet = ({
  expectationId,
  visibleContent,
  otherContent,
  status,
  payment,
}) => {
  const dispatch = useDispatch();
  const { setBlockerDimension } = useViewPort();

  const containerRef = useRef();
  const visibleContainerRef = useRef();

  const [springValue, api] = useSpring(() => ({ y: 0 }));
  const [md] = useMediaQuery('(min-width: 48em)');
  const [isExpanded, setIsExpanded] = useState(md);
  const [visibleContainerHeight, setVisibleContainerHeight] = useState(0);

  const visibleContentChild = useMemo(() => visibleContent(isExpanded), [
    visibleContent,
    isExpanded,
  ]);

  const shrink = useCallback(
    ({ immediate = false, hideMessage = false } = {}) => {
      if (isPendingStatus(status) && !hideMessage) {
        Message.config({ top: DEFAULT_MESSAGE_MARGIN_TOP });
        Message.info(I18n.t('dragSheet.expandedHeader.blockMapPrompt'));
        return;
      }
      const containerHeight = getDimensionFromRef(containerRef).height;
      const shrinkHeight = containerHeight - visibleContainerHeight;

      containerRef.current.scrollTo(0, 0);
      api.start({ y: shrinkHeight, immediate });
      setIsExpanded(false);
    },
    [status, visibleContainerHeight, containerRef],
  );

  const expand = useCallback(({ immediate = false } = {}) => {
    api.start({ y: 0, immediate });
    setIsExpanded(true);
  }, []);

  useEffect(
    () => {
      if (md || isPendingStatus(status) || isExpanded) {
        Message.destroy();
        expand({ immediate: true });
      } else {
        shrink({ immediate: true });
      }
    },
    [md, isExpanded, expand, shrink, status],
  );

  useEffect(
    () => {
      if (visibleContainerRef) {
        const height = getDimensionFromRef(visibleContainerRef).height;
        setVisibleContainerHeight(height);
      }
    },
    [visibleContainerRef, visibleContentChild],
  );

  const showDragHandle = !isExpanded && !md;

  // Only use in small screen (<768px), for drag up only
  const dragBind = useDrag(
    ({ last, movement: [, movementY], swipe: [, swipeY] }) => {
      const containerHeight = getDimensionFromRef(containerRef).height;

      if (!last) {
        const translateY = containerHeight - visibleContainerHeight + movementY;
        api.start({ y: translateY });
        return;
      }

      if (swipeY < 0) {
        expand();
        return;
      }

      const remainingHalfHeight =
        (containerHeight - visibleContainerHeight) / 3;

      if (movementY < 0 && Math.abs(movementY) > remainingHalfHeight) {
        expand();
      } else {
        shrink();
      }
    },
    {
      enabled: !isExpanded,
    },
  );

  useEffect(
    () => {
      setBlockerDimension({
        width: md ? getDimensionFromRef(visibleContainerRef).width : 0,
        height: md ? 0 : getDimensionFromRef(visibleContainerRef).height,
      });
    },
    [md],
  );

  /* -----ONLY TO FIX IOS DOUBLE WEBVIEW UNEXPECTED BEHAVIOUR----- */
  const initialContainerHeight = useRef();

  useEffect(() => {
    const handleResize = () => {
      const containerHeight = getDimensionFromRef(containerRef).height;
      if (initialContainerHeight.current === 0 && containerHeight > 0) {
        dispatch(mapDuck.creators.focusMap());
        if (isExpanded) {
          expand({ immediate: true });
        } else {
          shrink({ hideMessage: true, immediate: true });
        }

        setBlockerDimension({
          width: md ? getDimensionFromRef(visibleContainerRef).width : 0,
          height: md ? 0 : getDimensionFromRef(visibleContainerRef).height,
        });
      }
    };
    initialContainerHeight.current = getDimensionFromRef(containerRef).height;
    window.addEventListener('resize', handleResize, { once: true });

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  /* ------------------------------------------------------------- */
  return (
    <Fragment>
      <div
        className={styles.backdrop}
        style={{
          display: isExpanded && !md ? 'block' : 'none',
        }}
      />
      <animated.div
        className={styles.container}
        ref={containerRef}
        style={{
          y: springValue.y,
          overflow: isExpanded ? 'auto' : 'visible',
        }}
      >
        <div className={styles.content}>
          {isExpanded && !md && <ExpandedHeader onShrink={shrink} />}

          <div ref={visibleContainerRef}>
            {(md || !isExpanded) && (
              <ExpectationManagement
                id={expectationId}
                payment={payment}
                status={status}
              />
            )}

            <div>
              {showDragHandle && (
                <div
                  aria-label="dragger"
                  className={styles.dragger}
                  {...dragBind()}
                >
                  <div className={styles.dragIndicator} />
                </div>
              )}

              {visibleContentChild}
            </div>
          </div>

          {otherContent}
        </div>
      </animated.div>
    </Fragment>
  );
};

DragSheet.propTypes = {
  expectationId: PropTypes.number,
  visibleContent: PropTypes.func,
  otherContent: PropTypes.node,
  userType: PropTypes.shape({
    isReceiver: PropTypes.bool,
    isSender: PropTypes.bool,
  }),
  payment: PropTypes.shape({
    symbol: PropTypes.string,
    codAmount: PropTypes.bool,
    payment: PropTypes.shape({
      cod: PropTypes.string,
      symbol: PropTypes.string,
      deliveryFee: PropTypes.string,
      total: PropTypes.string,
    }),
  }),
  status: PropTypes.string,
};

DragSheet.defaultProps = {
  expectationId: 0,
  visibleContent: () => {},
  otherContent: null,
  userType: {
    isSender: false,
    isReceiver: true,
  },
  payment: {
    cod: '',
    symbol: '',
    deliveryFee: '',
    total: '',
  },
  status: '',
};

const ExpandedHeader = ({ onShrink }) => {
  const navigate = useNavigate();
  const { orderId, trackingPageType, trackingSource } = useSelector(
    instrumentationDuck.selectors.instrumentation,
  );

  const sendInstrumentation = useCallback(
    () => {
      scribeTrack.expressTracking.getSupport({
        trackingSource,
        orderIdDisplayed: orderId,
        isInApp: inGrabApp(),
        trackingPageType,
      });
    },
    [orderId, trackingPageType, trackingSource],
  );

  const navigateToSupportPage = useCallback(
    () => {
      sendInstrumentation();
      navigate(Routes.faq.path);
    },
    [navigate, sendInstrumentation],
  );

  return (
    <div className={styles.expandedHeader}>
      <Button className={styles.shrinkButton} onClick={onShrink}>
        <img src={arrowDownSvg} alt="arrow down" />
      </Button>

      <Button className={styles.supportButton} onClick={navigateToSupportPage}>
        {I18n.t('dragSheet.expandedHeader.getSupport')}
      </Button>
    </div>
  );
};

ExpandedHeader.propTypes = {
  onShrink: PropTypes.func,
};

ExpandedHeader.defaultProps = {
  onShrink: () => {},
};

export default DragSheet;
